import { makeAutoObservable } from 'mobx'
import { IntegrationKey, type Integration } from 'entities/Integrations'
import type { IChatbotField } from 'entities/Chatbot'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ManageIntegrationConnectionStore } from 'features/ManageIntegrationConnection'
import {
  formatWidgetMessage,
  INTEGRATION_DISPLAY_NAME,
} from 'pages/chatbot/ui/UnifiedAgent/lib/formatWidgetMessage'
import type { ChatStore } from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/store/chatStore'
import type { InstructionsSectionStore } from './instructionsSectionStore'

export type IInstructionsFieldItem = {
  key: string
  name: string
  integrationKey: IntegrationKey
  isConnected: boolean
  shouldReconnect: boolean
  integration: Integration | null
  chatbotField: IChatbotField
}

const TOTAL_STEPS = 3

export const STEP_HEADERS = [
  'Would you like to collect any contact fields?',
  'Which contact fields should the agent collect?',
  'What should the agent do with the collected information?',
]

export const COLLECT_FIELDS_OPTIONS = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
]

const EXCLUDE_FIELDS_INTEGRATIONS: Partial<Record<IntegrationKey, string[]>> = {
  [IntegrationKey.salesmessage]: ['number'],
}

const SUPPORTED_INTEGRATION_KEYS = [
  IntegrationKey.salesmessage,
  IntegrationKey.hubspot,
  IntegrationKey.calendly,
]

export class InstructionsWidgetStore {
  step = 1
  focusedIndex = 0
  search = ''
  selectedFieldKeys = new Set<string>()
  promptText = ''
  shouldCollectFields = false
  private _manageConnectionStore = new ManageIntegrationConnectionStore()

  constructor(
    readonly sectionStore: InstructionsSectionStore,
    readonly chatStore: ChatStore,
    readonly entities: IEntitiesStore
  ) {
    makeAutoObservable(this)
  }

  get totalSteps() {
    return this.shouldCollectFields ? TOTAL_STEPS : 1
  }

  get isLastStep() {
    return this.step === TOTAL_STEPS && this.shouldCollectFields
  }

  get allFields() {
    const result: IInstructionsFieldItem[] = []

    const pushFields = (integrationKey: IntegrationKey) => {
      const fields = this.entities.mergeFieldStore.getMergeFieldsIntegration(integrationKey)
      if (!fields?.size) return

      const isSm = integrationKey === IntegrationKey.salesmessage
      const integration = isSm
        ? null
        : this.entities.integrationsStore.getIntegration(integrationKey)
      const isConnected = isSm || (integration?.isConnected ?? false)
      const shouldReconnect = !isSm && (integration?.shouldReconnect ?? false)

      const excludedNames: string[] = EXCLUDE_FIELDS_INTEGRATIONS[integrationKey] ?? []

      fields.forEach((field) => {
        if (excludedNames.includes(field.name)) return
        const key = field.template.replace(/[{}]/g, '').trim()
        result.push({
          key,
          name: field.label,
          integrationKey,
          isConnected,
          shouldReconnect,
          integration,
          chatbotField: { key, name: field.label, type: field.typeChatbotField },
        })
      })
    }

    SUPPORTED_INTEGRATION_KEYS.forEach(pushFields)

    return result
  }

  get filteredFields() {
    if (!this.search.trim()) return this.allFields

    return this.allFields.filter((field) =>
      field.name.toLowerCase().includes(this.search.toLowerCase())
    )
  }

  get selectedFields() {
    return this.allFields.filter((f) => this.selectedFieldKeys.has(f.key))
  }

  get isNextDisabled() {
    if (this.isLastStep) return !this.promptText.length
    return this.selectedFieldKeys.size === 0
  }

  get currentListLength() {
    return this.step === 1 ? COLLECT_FIELDS_OPTIONS.length : this.filteredFields.length
  }

  get safeIndex() {
    if (!this.currentListLength) return 0
    return Math.min(this.focusedIndex, this.currentListLength - 1)
  }

  private get _selectedFieldsLabel() {
    return this.allFields
      .filter((field) => this.selectedFieldKeys.has(field.key))
      .map((field) => {
        const label = INTEGRATION_DISPLAY_NAME[field.integrationKey]
        return label ? `${field.name} (${label})` : field.name
      })
      .join(', ')
  }

  setFocusedIndex = (index: number) => {
    this.focusedIndex = index
  }

  moveFocus = (delta: number) => {
    if (!this.currentListLength) return
    this.focusedIndex = Math.min(Math.max(this.safeIndex + delta, 0), this.currentListLength - 1)
  }

  setSearch = (value: string) => {
    this.search = value
    this.focusedIndex = 0
  }

  setPromptText = (value: string) => {
    this.promptText = value
  }

  toggleField = (key: string) => {
    if (this.selectedFieldKeys.has(key)) {
      this.selectedFieldKeys.delete(key)
    } else {
      this.selectedFieldKeys.add(key)
    }
  }

  toggleFocused = () => {
    const field = this.filteredFields[this.safeIndex]
    if (field) this.toggleField(field.key)
  }

  advanceStep = () => {
    this.step++
    this.focusedIndex = 0
    this.search = ''
  }

  goBack = () => {
    if (this.step <= 1) return
    this.step--
    this.focusedIndex = 0
    this.search = ''
  }

  reset = () => {
    this.step = 1
    this.focusedIndex = 0
    this.search = ''
    this.selectedFieldKeys = new Set()
    this.promptText = ''
    this.shouldCollectFields = false
  }

  handleSelect = () => {
    if (this.step === 1) {
      const option = COLLECT_FIELDS_OPTIONS[this.safeIndex]
      if (option.id === 'no') {
        const message = formatWidgetMessage([{ q: STEP_HEADERS[0], a: 'No' }])
        this.chatStore.sendMessage(message)
        return
      }
      this.shouldCollectFields = true
      this.advanceStep()
    } else if (this.step === 2) {
      this.toggleFocused()
    }
  }

  selectAt = (index: number) => {
    this.focusedIndex = index
    this.handleSelect()
  }

  reconnect = (integration: Integration) => {
    this._manageConnectionStore.connect(integration)
  }

  handleClose = () => {
    const headers = this.step === 1 ? [STEP_HEADERS[0]] : STEP_HEADERS
    const message = formatWidgetMessage(headers.map((header) => ({ q: header, a: 'Skip' })))
    this.chatStore.sendMessage(message)
  }

  handleSubmit = () => {
    this.sectionStore.setContactFields(this.selectedFields.map((field) => field.chatbotField))
    const message = formatWidgetMessage([
      { q: STEP_HEADERS[0], a: 'Yes' },
      { q: STEP_HEADERS[1], a: this._selectedFieldsLabel },
      { q: STEP_HEADERS[2], a: this.promptText.trim() },
    ])

    this.chatStore.sendMessage(message)
  }
}
